/**
 * IntermissionOverlay — the "grab some popcorn" bridge shown between
 * two movies in the Movie Cue "Play all" flow. v1.1.1.
 *
 * Phases (in order):
 *   1. TRAILERS — up to 3 YouTube pre-rolls (2 current-year + 1 older
 *      classic). Each auto-advances via the YouTube IFrame Player API
 *      onStateChange = ENDED (0). Skip Trailers button available at
 *      any time.
 *   2. COUNTDOWN — dim next-movie poster + "grab some popcorn 🍿" +
 *      live 00:00 timer for the user-configured 5/10/15 minutes.
 *
 * A single Skip button always exits the whole intermission (trailers
 * + countdown) and starts the next movie immediately.
 */
import React, { useEffect, useRef, useState } from "react";
import { Popcorn, SkipForward, Loader2 } from "lucide-react";
import { fetchRandomTrailer } from "../lib/playerApi";

function fmtMinSec(totalSec) {
  const s = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

// Load the YouTube IFrame Player API script once per page-lifetime.
// Returns a promise that resolves when `window.YT.Player` is ready.
let _ytApiPromise = null;
function loadYoutubeApi() {
  if (_ytApiPromise) return _ytApiPromise;
  _ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev && prev();
      resolve(window.YT);
    };
    // Only inject the script tag once.
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      tag.async = true;
      document.head.appendChild(tag);
    }
  });
  return _ytApiPromise;
}

export default function IntermissionOverlay({
  minutes = 10,
  nextMovie,
  onFinish,
  onSkip,
}) {
  const [phase, setPhase] = useState("loading"); // loading | trailers | countdown
  const [trailers, setTrailers] = useState([]);
  const [trailerIdx, setTrailerIdx] = useState(0);
  const [remaining, setRemaining] = useState(minutes * 60);
  const playerRef = useRef(null);
  const containerRef = useRef(null);

  // ---- 1. Fetch three trailers on mount --------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Pull 3 random trailers: 2 from the current year, 1 from an older year.
      const curYear = new Date().getFullYear();
      // Random older year between 1980 and (currentYear - 10) — gives a
      // nice mix of 80s/90s/2000s classics.
      const olderYear = 1980 + Math.floor(Math.random() * (curYear - 10 - 1980));
      const [t1, t2, t3] = await Promise.all([
        fetchRandomTrailer(curYear),
        fetchRandomTrailer(curYear),
        fetchRandomTrailer(olderYear),
      ]);
      if (cancelled) return;
      const list = [t1, t2, t3].filter(Boolean);
      // Dedupe by yt_key in case current-year happened to return the same movie twice.
      const seen = new Set();
      const uniq = list.filter((t) => {
        if (seen.has(t.yt_key)) return false;
        seen.add(t.yt_key);
        return true;
      });
      setTrailers(uniq);
      // If we got zero trailers (offline / no TMDB key), skip straight to countdown.
      setPhase(uniq.length > 0 ? "trailers" : "countdown");
    })();
    return () => { cancelled = true; };
  }, []);

  // ---- 2. YouTube player controller ------------------------------
  useEffect(() => {
    if (phase !== "trailers") return undefined;
    const key = trailers[trailerIdx]?.yt_key;
    if (!key) {
      // Ran out of trailers — start the countdown.
      setPhase("countdown");
      return undefined;
    }
    let destroyed = false;
    loadYoutubeApi().then((YT) => {
      if (destroyed || !containerRef.current) return;
      // Replace any existing player.
      try { playerRef.current?.destroy(); } catch { /* noop */ }
      playerRef.current = new YT.Player(containerRef.current, {
        videoId: key,
        playerVars: {
          autoplay: 1,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          fs: 0,
          playsinline: 1,
          iv_load_policy: 3,
        },
        events: {
          onReady: (e) => {
            try { e.target.playVideo(); } catch { /* noop */ }
          },
          onStateChange: (e) => {
            // 0 = ENDED
            if (e.data === 0) {
              // Advance to the next trailer or fall through to countdown.
              setTrailerIdx((i) => {
                const next = i + 1;
                if (next >= trailers.length) {
                  // No more trailers → countdown.
                  setPhase("countdown");
                }
                return next;
              });
            }
          },
          onError: () => {
            // If YouTube refuses to play (region-blocked, etc.),
            // advance to next trailer.
            setTrailerIdx((i) => {
              const next = i + 1;
              if (next >= trailers.length) setPhase("countdown");
              return next;
            });
          },
        },
      });
    });
    return () => {
      destroyed = true;
      try { playerRef.current?.destroy(); } catch { /* noop */ }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, trailerIdx, trailers]);

  // ---- 3. Countdown tick -----------------------------------------
  useEffect(() => {
    if (phase !== "countdown") return undefined;
    if (remaining <= 0) {
      onFinish?.();
      return undefined;
    }
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(id);
          setTimeout(() => onFinish?.(), 50);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const posterUrl =
    nextMovie?.logo_url || nextMovie?.poster || nextMovie?.cover || null;

  return (
    <div
      data-testid="movie-cue-intermission"
      className="fixed inset-0 z-[9998] flex items-center justify-center overflow-hidden bg-black text-white"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSkip?.();
        }
      }}
    >
      {/* ---- Trailer phase: full-bleed YouTube ------------------- */}
      {phase === "trailers" && (
        <>
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            {/* IFrame API mounts INTO this div and replaces it. */}
            <div className="w-full h-full max-w-[100vw] max-h-[100vh]">
              <div ref={containerRef} className="w-full h-full" />
            </div>
          </div>
          <div className="absolute top-4 left-4 z-10 text-xs uppercase tracking-widest text-white/80 bg-black/50 px-3 py-1.5 rounded-full backdrop-blur-sm">
            Trailer {trailerIdx + 1} of {trailers.length}
          </div>
          <button
            data-testid="movie-cue-intermission-skip-trailer"
            onClick={() => {
              try { playerRef.current?.stopVideo(); } catch { /* noop */ }
              setTrailerIdx((i) => {
                const next = i + 1;
                if (next >= trailers.length) setPhase("countdown");
                return next;
              });
            }}
            className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white text-xs backdrop-blur-sm border border-white/10"
          >
            <SkipForward className="w-3.5 h-3.5" />
            Skip trailer
          </button>
          <button
            data-testid="movie-cue-intermission-skip-all"
            onClick={() => {
              try { playerRef.current?.stopVideo(); } catch { /* noop */ }
              onSkip?.();
            }}
            autoFocus
            className="absolute bottom-6 right-6 z-10 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black font-medium text-sm hover:bg-slate-200 focus:ring-2 focus:ring-rose-400 shadow-lg"
          >
            Skip intermission — play now
          </button>
        </>
      )}

      {/* ---- Loading spinner ------------------------------------- */}
      {phase === "loading" && (
        <div className="flex flex-col items-center gap-3 text-slate-300">
          <Loader2 className="w-8 h-8 animate-spin text-rose-400" />
          <div className="text-sm">Preparing intermission…</div>
        </div>
      )}

      {/* ---- Countdown phase ------------------------------------- */}
      {phase === "countdown" && (
        <>
          {posterUrl && (
            <img
              src={posterUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-25 blur-md scale-110"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black" />
          <div className="relative z-10 max-w-lg w-[92%] rounded-2xl border border-white/10 bg-black/50 backdrop-blur-md p-8 sm:p-10 text-center shadow-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-400/30 text-rose-200 text-xs font-medium mb-6">
              <Popcorn className="w-3.5 h-3.5" />
              Intermission — grab some popcorn 🍿
            </div>
            <div
              data-testid="movie-cue-intermission-countdown"
              className="font-mono text-6xl sm:text-7xl font-bold tabular-nums text-white mb-2 tracking-tight"
            >
              {fmtMinSec(remaining)}
            </div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-8">
              until next feature
            </div>
            <div className="mb-8">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
                Up next
              </div>
              <div
                data-testid="movie-cue-intermission-next-title"
                className="text-xl sm:text-2xl font-semibold text-white line-clamp-2"
              >
                {nextMovie?.name || "Next feature"}
              </div>
              {nextMovie?.year && (
                <div className="text-xs text-slate-400 mt-1">{nextMovie.year}</div>
              )}
            </div>
            <button
              data-testid="movie-cue-intermission-skip"
              onClick={onSkip}
              autoFocus
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black hover:bg-slate-200 focus:bg-slate-200 focus:ring-2 focus:ring-rose-400 font-medium transition-colors"
            >
              <SkipForward className="w-4 h-4" />
              Skip intermission — play now
            </button>
          </div>
        </>
      )}
    </div>
  );
}
